import { DELETE, GET, PATCH, POST } from 'utils/api';
import { LOB_PERSONAL_AUTO, LOB_PERSONAL_PROPERTY } from 'variables/constants';

import type { APIMarketplaceGroupQuote } from 'types/api.types';

export interface QuoteLock {
  is_locked: boolean;
  policy_uuid: string | null;
  submission_id: number | null;
  locked_at: string | null;
  can_edit: boolean;
  broker_name: string;
}

export type ContactSearchType =
  | 'name'
  | 'email'
  | 'postal_code'
  | 'phone'
  | 'trading_name'
  | 'contact_person';

export interface ActurisContact {
  contact_ref: string;
  name: string;
  address: string;
  postcode: string;
  date_of_birth: string;
}

export interface ActurisContactListResponse {
  results: ActurisContact[];
  count: number;
  supported_search_types: ContactSearchType[];
}

export interface ActurisContactDetails {
  contact_ref: string;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string | null;
  contact_status_date: string | null;
  email: string | null;
  home_phone: string;
  mobile_phone: string;
  work_phone: string;
  street_address: string;
  street_address_2: string;
  city: string;
  county: string;
  province: number | null;
  postal_code: string;
  country: string;
  office_ref?: string;
  office_name?: string;
  executive_ref?: string;
  executive_name?: string;
  handler_ref?: string;
  handler_name?: string;
  business_name?: string;
  business_number?: string;
  approved_contacts?: string;
  client_code?: string;
  branch_code?: string;
  display_name?: string;
  comment?: string;
}

export type ContactSearchStatus = 'idle' | 'pending' | 'complete' | 'error';

export interface ContactSearchResponse {
  status: ContactSearchStatus;
  results?: ActurisContact[];
  count?: number;
  search?: string;
  search_type?: string;
  error?: string;
  supported_search_types: ContactSearchType[];
}

export const apiTriggerContactSearch = (
  search: string,
  searchType: ContactSearchType = 'name',
  page: number = 1,
  pageSize: number = 50,
): Promise<ContactSearchResponse> => {
  return POST('api/acturis-customers/contacts/search/', {
    search,
    search_type: searchType,
    page,
    page_size: pageSize,
  });
};

export const apiPollContactSearch = (
  search: string,
): Promise<ContactSearchResponse> => {
  const params = new URLSearchParams({ search });
  return GET(`api/acturis-customers/contacts/search/?${params.toString()}`);
};

export const apiInitiateGetContactDetails = (
  contactRef: string,
): Promise<{ operation_id: number; status: 'pending' }> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/`);
};

export type ContactOperationStatus =
  | 'pending'
  | 'complete'
  | 'error'
  | 'duplicates_found';

export interface DuplicateContact {
  contact_ref: string;
  contact_name: string;
  email: string;
  matches: number;
}

export interface ContactOperationResponse {
  operation_id: number;
  status: ContactOperationStatus;
  contact_ref?: string;
  contact?: ActurisContactDetails;
  error_message?: string;
  duplicates?: DuplicateContact[];
}

export interface ContactCreateData {
  first_name: string;
  last_name: string;
  date_of_birth?: string | null;
  email?: string;
  home_phone?: string;
  mobile_phone?: string;
  street_address?: string;
  city?: string;
  province?: number | null;
  postal_code?: string;
}

export const apiCreateContact = (
  data: ContactCreateData,
): Promise<{ operation_id: number }> => {
  return POST('api/acturis-customers/contacts/', data);
};

export const apiUpdateContact = (
  contactRef: string,
  data: Partial<ContactCreateData>,
): Promise<{ operation_id: number }> => {
  return PATCH(`api/acturis-customers/contacts/${contactRef}/`, data);
};

export const apiGetContactOperationStatus = (
  operationId: number,
): Promise<ContactOperationResponse> => {
  return GET(`api/acturis-customers/contacts/operations/${operationId}/`);
};

export type ContactRfqsStatus = 'idle' | 'pending' | 'complete' | 'error';

export type MergedQuoteSource =
  | 'matched'
  | 'acturis_only'
  | 'db_only'
  | 'local_draft';

export interface MergedQuote {
  source: MergedQuoteSource;
  quote_id: number | null;
  rfq_ref: string | null;
  line_of_business: number | null;
  line_of_business_display: string;
  effective_date: string | null;
  rfq_status: string;
  premium: string | null;
  carrier_name: string | null;
  quoted_at: string | null;
  created_at: string | null;
  acturis_rfq_status: string | null;
  acturis_premium: string | null;
  product_target: string | null;
  group_id: number | null;
  can_edit: boolean;
  broker_name: string;
}

export interface ContactRfqsResponse {
  status: ContactRfqsStatus;
  quotes?: MergedQuote[];
  count?: number;
  contact_ref?: string;
  error?: string;
}

export const apiTriggerContactRfqs = (
  contactRef: string,
  options?: {
    productTarget?: string;
    effectiveDateFrom?: string;
    effectiveDateTo?: string;
  },
): Promise<{ status: 'pending'; contact_ref: string }> => {
  return POST(`api/acturis-customers/contacts/${contactRef}/rfqs/`, {
    product_target: options?.productTarget,
    effective_date_from: options?.effectiveDateFrom,
    effective_date_to: options?.effectiveDateTo,
  });
};

export const apiPollContactRfqs = (
  contactRef: string,
): Promise<ContactRfqsResponse> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/rfqs/`);
};

export const apiGetAutoQuotePolicyInfo = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/policy-info/`);
};

export const apiGetAutoQuoteVehicles = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/vehicles/`);
};

export const apiGetAutoQuoteDrivers = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/drivers/`);
};

export const apiGetAutoQuoteAssignment = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/assignment/`);
};

export const apiGetAutoQuoteCarriers = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/carriers/`);
};

export const apiGetAutoQuoteValidations = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/validations/`);
};

export const apiGetAutoQuoteMcValidations = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/mc-validations/`);
};

export const apiGetAutoQuoteSummary = (id: number) => {
  return GET(`api/quote-marketplace/auto/quotes/${id}/summary/`);
};

export const apiGetPropertyQuotePolicyInfo = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/policy-info/`);
};

export const apiGetPropertyQuoteLocations = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/locations/`);
};

export const apiGetPropertyQuoteGeneral = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/general/`);
};

export const apiGetPropertyQuoteAdditional = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/additional/`);
};

export const apiGetPropertyQuoteCarriers = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/carriers/`);
};

export const apiGetPropertyQuoteValidations = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/validations/`);
};

export const apiGetPropertyQuoteMcValidations = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/mc-validations/`);
};

export const apiGetPropertyQuoteSummary = (id: number) => {
  return GET(`api/quote-marketplace/property/quotes/${id}/summary/`);
};

export interface QuotePolicyData {
  policy_uuid: string;
  submission_id: number;
}

export const apiCreateAutoQuotePolicy = (
  id: number,
): Promise<QuotePolicyData> => {
  return POST(`api/quote-marketplace/auto/quotes/${id}/policy/`, {});
};

export const apiCreatePropertyQuotePolicy = (
  id: number,
): Promise<QuotePolicyData> => {
  return POST(`api/quote-marketplace/property/quotes/${id}/policy/`, {});
};

export const apiCreateGroupPolicy = (
  groupId: number,
): Promise<QuotePolicyData> => {
  return POST(`api/quote-marketplace/groups/${groupId}/policy/`, {});
};

export interface InitiateQuoteResponse {
  quote_id: number;
}

export interface InitiateQuoteParams {
  contactRef: string;
  lineOfBusiness: number;
  effectiveDate: string;
  locationType?: string;
  contactData?: Partial<ActurisContactDetails>;
  termMonths?: 6 | 12;
}

export const apiInitiateQuote = ({
  contactRef,
  lineOfBusiness,
  effectiveDate,
  locationType,
  contactData,
  termMonths,
}: InitiateQuoteParams): Promise<InitiateQuoteResponse> => {
  return POST('api/quote-marketplace/quotes/initiate/', {
    contact_ref: contactRef,
    line_of_business: lineOfBusiness,
    effective_date: effectiveDate,
    ...(locationType ? { location_type: locationType } : {}),
    ...(contactData ? { contact_data: contactData } : {}),
    ...(termMonths ? { term_months: termMonths } : {}),
  });
};

export interface InitiateRFQQuoteParams {
  contactRef: string;
  lineOfBusiness: number;
  productTarget: string;
  effectiveDate: string;
  contactData?: Partial<ActurisContactDetails>;
}

export const apiInitiateRFQQuote = ({
  contactRef,
  lineOfBusiness,
  productTarget,
  effectiveDate,
  contactData,
}: InitiateRFQQuoteParams): Promise<{ quote_id: number; rfq_ref: string }> =>
  POST('api/quote-marketplace/quotes/initiate-rfq/', {
    contact_ref: contactRef,
    line_of_business: lineOfBusiness,
    product_target: productTarget,
    effective_date: effectiveDate,
    ...(contactData ? { contact_data: contactData } : {}),
  });

export interface RFQAttachment {
  file_name: string;
  file_size: number;
  document_ref: string;
}

export interface RFQPolicyInfo {
  quote_id: number;
  contact_ref: string;
  rfq_ref: string;
  line_of_business: number;
  product_target: ProductTargetOption | null;
  effective_date: string;
  quote_description: string;
  client_requirements: string;
  attachments: RFQAttachment[];
}

export interface RFQPolicyInfoSaveResponse extends RFQPolicyInfo {
  /** Number of the submitted files filed to the RFQ, in order, before any failure. */
  files_filed: number;
  error?: string;
}

export const apiGetRFQPolicyInfo = (quoteId: number): Promise<RFQPolicyInfo> =>
  GET(`api/quote-marketplace/quotes/${quoteId}/rfq-policy-info/`);

export const apiSaveRFQPolicyInfo = (
  quoteId: number,
  values: {
    quoteDescription: string;
    clientRequirements: string;
    files: File[];
  },
): Promise<RFQPolicyInfoSaveResponse> => {
  const formData = new FormData();
  formData.append('quote_description', values.quoteDescription);
  formData.append('client_requirements', values.clientRequirements);
  values.files.forEach((file) => formData.append('files', file));
  return POST(
    `api/quote-marketplace/quotes/${quoteId}/rfq-policy-info/`,
    formData,
  );
};

export interface QuoteInitiationStatus {
  id: number;
  type: string;
  status: string;
  status_code: number;
  error_message: string;
  quote_id: number | null;
  created_at: string;
  updated_at: string;
}

export const apiGetQuoteInitiationStatus = (
  quoteId: number,
): Promise<QuoteInitiationStatus> => {
  return GET(`api/quote-marketplace/quotes/initiation-status/${quoteId}/`);
};

export interface DashReportDriver {
  name: string;
  license_number: string;
  status: 'not_ordered' | 'ordered' | 'available' | 'none';
  consent_given: boolean;
  consent_id: number | null;
  dash_log_id: number | null;
}

export interface PropertyPolicy {
  habitational_policy_type?: string;
  other_habitational_policy_type?: string;
  current_habitational_insurer?: string;
  other_current_habitational_insurer?: string;
  habitational_policy_number?: string;
  since?: string;
  occupied_since?: string;
}

export const apiUpdateAutoQuotePolicyInfo = (
  id: number,
  data: {
    province?: number;
    effective_date?: string;
    vehicle_type?: string;
    business_name_vehicles?: boolean;
    business_name?: string;
    term_6_month?: boolean;
    term_12_month?: boolean;
    policy_discount?: string;
    full_history_dash_dash_report_drivers?: DashReportDriver[];
    property_policy?: PropertyPolicy;
    insurance_history?: {
      continuously_insured_since?: string;
      year_policy_first_held_with_broker?: string;
      multi_policy_discount?: boolean;
      apply_for_all_carriers?: boolean;
      cancelled_for_non_payment?: boolean | null;
      num_cancellations?: number | null;
      date_of_last_cancellation?: string | null;
      property_current_insured_since?: string;
      current_habitational_insurer?: string;
      multi_property_discount?: boolean;
      auto_current_insured_since?: string;
      current_auto_insurer?: string;
      membership_discount_membership_group?: string;
      membership_discount_membership_number?: string;
      membership_discount_membership_effective_date?: string;
      additional_membership_discount_membership_group?: string;
      additional_membership_discount_membership_number?: string;
      additional_membership_discount_membership_effective_date?: string;
    };
  },
) => {
  return PATCH(`api/quote-marketplace/auto/quotes/${id}/policy-info/`, data);
};

export const apiCreateQuoteConsent = (
  quoteId: number,
  data: {
    type: number;
    method: number;
    license_number?: string;
    vehicle_vin?: string;
    vehicle_id?: number;
  },
) => {
  return POST(`api/quote-marketplace/quotes/${quoteId}/consent/`, data);
};

export const apiOrderDashReport = (quoteId: number, licenseNumber: string) => {
  return POST(`api/quote-marketplace/quotes/${quoteId}/order-dash/`, {
    license_number: licenseNumber,
  });
};

export const getDashReportPdfUrl = (quoteId: number, dashLogId: number) => {
  return `api/quote-marketplace/quotes/${quoteId}/dash-pdf/${dashLogId}/`;
};

export const getMvrReportPdfUrl = (quoteId: number, mvrRequestId: number) => {
  return `api/quote-marketplace/auto/quotes/${quoteId}/mvr-pdf/${mvrRequestId}/`;
};

export interface RegisteredOwner {
  first_name: string;
  last_name: string;
  business_name?: string;
  is_business?: boolean;
}

export interface VehicleGaraging {
  same_as_applicant: boolean;
  postal_code: string;
  city: string;
  street_number: string;
  street_name: string;
  parking_at_night: string;
  latitude: number | null;
  longitude: number | null;
}

export interface VehicleDistance {
  one_way_km: number;
  annual_km: number;
  business_km: number;
}

export interface VehicleValue {
  list_price_new: number | null;
  purchase_price: number | null;
}

export interface VehiclePurchaseInfo {
  condition: string;
  purchase_date: string | null;
  km_at_purchase: number | null;
}

export interface VehicleAntiTheft {
  device_types: string[];
  device_type_other: string;
  manufacturers: string[];
}

export interface VehicleDiscounts {
  single_vehicle_mvd: boolean;
  winter_tires: boolean;
}

export interface AccidentBenefits {
  income_replacement_enabled: boolean;
  income_replacement_limit_per_week_cad: string;
  income_replacement_limit_per_week_cad_other: number | null;
  catastrophic_impairment_enabled: boolean;
  catastrophic_impairment_limit_cad: string;
  non_catastrophic_impairment_enabled: boolean;
  non_catastrophic_impairment_limit_cad: string;
  caregiver_enabled: boolean;
  caregiver_limit_cad: number | null;
  caregiver_coverage_options: string[];
  housekeeping_home_maintenance_enabled: boolean;
  housekeeping_home_maintenance_limit_cad: number | null;
  housekeeping_home_maintenance_coverage_options: string[];
  death_benefit_enabled: boolean;
  death_benefit_limit_cad: number | null;
  funeral_benefit_enabled: boolean;
  funeral_benefit_limit_cad: number | null;
  dependant_care_enabled: boolean;
  dependant_care_limit_cad: number | null;
  non_earner_enabled: boolean;
  non_earner_limit_cad: number | null;
  lost_educational_expenses_enabled: boolean;
  lost_educational_expenses_limit_cad: number | null;
  expenses_of_visitors_enabled: boolean;
  expenses_of_visitors_limit_cad: number | null;
  damage_to_personal_items_enabled: boolean;
  damage_to_personal_items_limit_cad: number | null;
  indexation_benefit_enabled: boolean;
}

export interface VehicleCoverages {
  liability_limit_cad: string;
  liability_limit_other_cad?: number | null;
  dcpd_deductible_cad: string;
  dcpd_deductible_other_cad?: number | null;

  collision_enabled: boolean;
  collision_deductible_cad: string;
  collision_deductible_other_cad?: number | null;
  comprehensive_enabled: boolean;
  comprehensive_deductible_cad: string;
  comprehensive_deductible_other_cad?: number | null;
  all_perils_enabled: boolean;
  all_perils_deductible_cad: string;
  all_perils_deductible_other_cad?: number | null;

  // Endorsements
  opcf_2_enabled?: boolean;
  opcf_6a_passengers_enabled?: boolean;
  opcf_6a_described_use?: string;
  opcf_8_enabled?: boolean;
  opcf_8_limit_cad?: number | null;
  opcf_8a_enabled?: boolean;
  opcf_8a_limit_cad?: number | null;
  opcf_8a_deductible_cad?: number | null;
  opcf_4_min_enabled?: boolean;
  opcf_19a_enabled?: boolean;
  opcf_19a_insured_amount_cad?: number | null;

  opcf_20_enabled: boolean;
  opcf_20_limit_per_day_cad: number | null;
  opcf_20_disappearing_deductible?: boolean;

  opcf_27_enabled: boolean;
  opcf_27_limit_cad: number | null;

  opcf_35_enabled?: boolean;

  opcf_43_enabled: boolean;
  opcf_43_indemnity_period_months: number | null;

  lienholder_endorsement_enabled?: boolean;
  lessor_endorsement_enabled?: boolean;

  opcf_44_enabled?: boolean;
  opcf_48_enabled?: boolean;
  opcf_49_enabled?: boolean;

  waiver_of_deductible?: boolean;
  accident_waiver: boolean;
  minor_conviction_protection: boolean;
  carsharing_enabled?: boolean;

  ride_sharing_enabled: boolean;
  ride_sharing_companies: string[];

  accident_benefits?: AccidentBenefits;
  accident_benefits_confirmed?: boolean;
  all_coverages_confirmed?: boolean;

  // Province-specific accident benefits (NS/AB only)
  sef33_enabled?: boolean;
  sef48_enabled?: boolean;

  sef_13c_enabled?: boolean;
  sef_13d_enabled?: boolean;
  sef_49a_enabled?: boolean;
  cae_17_enabled?: boolean;
  cae_18_enabled?: boolean;
  sef_13h_enabled?: boolean;
}

export interface VinLookupVehicle {
  year: string;
  make: string;
  model: string;
  fuel_type: string;
  vicc_code: string;
  vehicle_ref: string;
}

export interface VinLookupResult {
  vin: string;
  status: 'pending' | 'found' | 'not_found' | 'error' | 'multiple_results';
  error?: string;
  year?: string;
  make?: string;
  model?: string;
  fuel_type?: string;
  vicc_code?: string;
  vehicle_ref?: string;
  all_results?: VinLookupVehicle[];
}

export interface Vehicle {
  id: number;
  registered_owners: RegisteredOwner[];
  vehicle_model_year: number | null;
  make: string;
  model: string;
  vin: string;
  vicc_code: string;
  vehicle_ref?: string;
  manufacturer_ref?: string;
  model_ref?: string;
  fuel_type: string;
  vehicle_body_type: string;
  cylinders: string;
  vehicle_ownership: string;
  garaging: VehicleGaraging;
  vehicle_use: string;
  distance: VehicleDistance;
  value: VehicleValue;
  purchase_info: VehiclePurchaseInfo;
  anti_theft: VehicleAntiTheft;
  safety_features: string[];
  safety_features_other: string;
  discounts: VehicleDiscounts;
  coverages: VehicleCoverages;
  vin_lookup_result?: VinLookupResult;
  vicc_available_years?: string[];
  principal_driver_id?: number | null;
  secondary_drivers?: number[];
  occasional_drivers?: number[];
  learner_drivers?: number[];
  telematics_enabled?: boolean;
}

export interface VehiclesData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  province: number;
  vehicles: Vehicle[];
  quotes_reset?: boolean;
  business_name?: string;
  business_name_vehicles?: boolean;
  dash_vehicle_vins?: string[];
  contact_province?: number | null;
}

export interface PriorAddress {
  id: number;
  address: string;
  unit_number?: string;
  city: string;
  province: string;
  postal_code: string;
}

export interface Insured {
  is_corporation?: boolean;
  retrieve_credit: string;
  privacy_consent: string;
  relation_to_insured: string;
  other_relation_to_insured: string;
  salutation: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  suffix: string;
  date_of_birth: string | null;
  home_phone: string;
  applicant_gender: string;
  occupation: string;
  other_occupation: string;
  employment_status: string;
  date_hired: string | null;
  mailing_address: string;
  mailing_unit_number?: string;
  mailing_city: string;
  mailing_province: string;
  mailing_postal_code: string;
  different_mailing_address: boolean;
  has_applicant_changed_address_in_the_last_3_years: string;
  prior_address: string;
  prior_unit_number?: string;
  prior_city: string;
  prior_province: string;
  prior_postal_code: string;
  prior_addresses: PriorAddress[];
}

export interface OilTank {
  id: number;
  construction_type: string;
  other_construction_type: string;
  location: string;
  heating_associated_with: string;
  make: string;
  model_year: number | null;
  installation_date: string | null;
  last_inspection_date: string | null;
  approved_by_ulc_csa_or_wh: boolean;
  professionally_installed: boolean;
  any_damage: boolean;
}

export interface Floater {
  id: number;
  item_type: string;
  item_description: string;
  item_value_cad: number | null;
  deductible_cad: number | null;
  purchase_date: string | null;
  valuation_date: string | null;
  serial_identification_number: string;
  coverage_required: string;
  item_exhibited: string;
  item_used_for_business: boolean;
}

export interface Watercraft {
  id: number;
  name: string;
  manufacturer: string;
  make_model: string;
  model_year_yyyy: number | null;
  description: string;
  length_ft: number | null;
  value_cad: number | null;
  horsepower: number | null;
  max_speed_mph: number | null;
  purchase_date: string | null;
  propulsion_type: string;
  mooring_location: string;
  watercraft_usage: string[];
  waters_navigated: string;
  rented_or_leased_to_others: boolean;
  describe_commercial_charter_use: string;
  number_of_weeks_rented_to_others_per_annum: number | null;
  has_trailer: boolean;
  trailer_value_cad: number | null;
  trailer_length_ft: number | null;
  trailer_make_model: string;
  trailer_manufacturer: string;
  trailer_model_year_yyyy: number | null;
  trailer_license_plate: string;
  trailer_serial_number: string;
  trailer_condition: string;
  trailer_purchase_date: string | null;
  trailer_is_road_licensed: boolean;
}

export interface Trailer {
  id: number;
  trailer_type: string;
  other_trailer_type: string;
  manufacturer: string;
  make_model: string;
  model_year_yyyy: number | null;
  length_ft: number | null;
  replacement_value_cad: number | null;
  license_plate: string;
  identification_serial_number: string;
  condition_when_purchased: string;
  purchase_date: string | null;
  number_of_operators: number | null;
  is_the_trailer_road_licensed: boolean;
  province_territory: string;
  occupied_by_the_owner: string;
  the_trailer_is_permanently_parked: string;
  park_name_if_applicable: string;
  equipment_and_contents_value_cad: number | null;
  hail_coverage_required: string;
  number_of_days_outside_of_canada_per_annum: number | null;
  rented_or_leased_to_others: boolean;
  number_of_weeks_rented_to_others_per_annum: number | null;
}

export interface LocationCoverages {
  coverage_form_type: string;
  deductible_cad: number | null;
  grbc_enabled: boolean;
  single_limit_enabled: boolean;
  bylaw_enabled: boolean;
  bylaw_limit_cad: number | null;
  bylaw_deductible_cad: number | null;
  fire_department_enabled: boolean;
  fire_department_limit_cad: number | null;
  fire_department_deductible_cad: number | null;
  identity_theft_enabled: boolean;
  identity_theft_limit_cad: number | null;
  identity_theft_deductible_cad: number | null;
  sewer_backup_enabled: boolean;
  sewer_backup_limit_cad: number | null;
  sewer_backup_deductible_cad: number | null;
  ground_water_enabled: boolean;
  ground_water_limit_cad: number | null;
  ground_water_deductible_cad: number | null;
  above_ground_water_enabled: boolean;
  above_ground_water_limit_cad: number | null;
  above_ground_water_deductible_cad: number | null;
  overland_water_enabled: boolean;
  overland_water_limit_cad: number | null;
  overland_water_deductible_cad: number | null;
  backflow_valve_enabled: boolean;
  sump_pump_pit_enabled: boolean;
  alarmed_sump_pump_enabled: boolean;
  service_line_enabled: boolean;
  service_line_limit_cad: number | null;
  service_line_deductible_cad: number | null;
  liability_limit_cad: number | null;
  environmental_deductible_cad: number | null;
  earthquake_enabled: boolean;
  earthquake_coverage_option: string;
  earthquake_limit_cad: number | null;
  earthquake_condo_enabled: boolean;
  earthquake_condo_limit_cad: number | null;
  collapse_enabled: boolean;
  collapse_limit_cad: number | null;
  mass_evacuation_enabled: boolean;
  mass_evacuation_limit_cad: number | null;
  home_based_business_enabled: boolean;
  home_based_business_type: string;
  home_based_business_gross_annual_income: number | null;
  home_based_business_num_employees: number | null;
  home_based_business_products_outside_canada: boolean;
  home_based_business_internet_sales: boolean;
  home_based_business_professional_activity: boolean;
  home_based_business_coverage_enabled: boolean;
  glass_buydown_enabled: boolean;
  residential_glass_enabled: boolean;
  personal_cyber_enabled: boolean;
  food_freezer_enabled: boolean;
  malicious_damage_enabled: boolean;
  residence_burglary_enabled: boolean;
  theft_and_burglary_enabled: boolean;
  vandalism_on_dwelling_enabled: boolean;
  vandalism_theft_by_tenants_enabled: boolean;
  rental_income_enabled: boolean;
  rental_income_limit_cad: number | null;
  rental_income_deductible_cad: number | null;
  personal_cyber_limit_cad: number | null;
  personal_cyber_deductible_cad: number | null;
  solar_panel_enabled: boolean;
  solar_panel_date_of_installation: string | null;
  solar_panel_installation_location: string;
  solar_panel_type: string;
  solar_panel_capacity: number | null;
  solar_panel_tracking: boolean;
  solar_panel_leased: boolean;
  solar_panel_tied_to_grid: boolean;
  solar_panel_limit_cad: number | null;
  solar_panel_deductible_cad: number | null;
  solar_panel_settlement_basis: string;
  swimming_pool_enabled: boolean;
  swimming_pool_type: string;
  swimming_pool_material: string;
  swimming_pool_year_built: string | null;
  swimming_pool_fenced: boolean | null;
  property_in_storage_enabled: boolean;
  property_in_storage_include_burglary: boolean;
  property_in_storage_coverage_type: string;
  property_in_storage_value_cad: number | null;
  property_in_storage_percent_to_insure: number | null;
  hail_peril_adjustment_enabled: boolean;
  hail_peril_adjustment_limit_cad: number | null;
  hail_peril_adjustment_deductible_cad: number | null;
  wind_peril_adjustment_enabled: boolean;
  wind_peril_adjustment_limit_cad: number | null;
  wind_peril_adjustment_deductible_cad: number | null;
  water_peril_adjustment_enabled: boolean;
  water_peril_adjustment_limit_cad: number | null;
  water_peril_adjustment_deductible_cad: number | null;
  claim_free_protection_endorsement: boolean;
}

export interface PlumbingMaterial {
  type: string;
  percentage: number | null;
}

export interface Location {
  id: number;
  location_type: string;
  floor_unit_located_on: number | null;
  tenant_condo_building_foundation_type: string;
  tenant_condo_exterior_wall_material_type: string;
  tenant_condo_roofing_material_type: string;
  address: string;
  unit_number?: string;
  city: string;
  province: string;
  postal_code: string;
  number_of_mortgages: number | null;
  address_validated: boolean;
  validated_street_number: string;
  validated_street_name: string;
  validated_city: string;
  validated_province: string;
  validated_postal_code: string;
  insured: Insured;
  co_insureds: Insured[];
  metres_to_hydrant: string;
  kilometres_to_firehall: number | null;
  unprotected: boolean;
  evaluation_date: string | null;
  dwelling_use: string;
  location_accessibility: string;
  other_location_accessibility: string;
  home_evaluation_tool_used: string;
  building_value: number | null;
  building_coverage: number | null;
  outbuildings: number | null;
  contents: number | null;
  construction_type: string;
  other_construction_type: string;
  construction_type_aluminum_percentage?: number | null;
  construction_type_brick_percentage?: number | null;
  construction_type_concrete_percentage?: number | null;
  construction_type_fibreglass_percentage?: number | null;
  frame_wood_percentage?: number | null;
  log_percentage?: number | null;
  masonite_percentage?: number | null;
  construction_type_monocoque_percentage?: number | null;
  construction_type_steel_percentage?: number | null;
  construction_type_stone_percentage?: number | null;
  construction_type_other_percentage?: number | null;
  exterior_wall_type: string;
  other_exterior_wall_type: string;
  property_type: string;
  other_property_type: string;
  garage_type: string;
  other_garage_type: string;
  number_of_cars: number | null;
  number_of_storeys: string;
  storeys: number | null;
  building_shape: string;
  number_of_selfcontained_units_in_property: number | null;
  number_of_families: number | null;
  number_of_full_bathrooms: number | null;
  number_of_half_bathrooms: number | null;
  number_of_kitchens: string;
  kitchens: number | null;
  other_location_type: string;
  eldest_occupant_birthdate: string | null;
  year_built_yyyy: number | null;
  occupancy_date: string | null;
  occupancy: string;
  occupants_relation_to_insured?: string;
  resident_smokers: string;
  in_law_suite: boolean;
  first_time_homebuyer: string;
  number_of_dogs_in_the_household: number | null;
  breeds_of_dogs: string[];
  total_living_area_sq_ft: number | null;
  basement_area_sq_ft: number | null;
  area_unit: string;
  building_foundation_type: string;
  building_foundation_material_type: string;
  basement_percentage: number | null;
  crawlspace_percentage: number | null;
  post_pier_on_slab_percentage: number | null;
  slab_concrete_slab_percentage: number | null;
  walkout_basement_percentage: number | null;
  building_foundation_type_other_percentage: number | null;
  other_building_foundation_type: string;
  finished_basement_percentage: number | null;
  burglar_alarm: string;
  fire_alarm: string;
  block_watch: boolean;
  walled_community: boolean;
  deadbolt_locks: boolean;
  bars_on_windows: boolean;
  sprinklers: boolean;
  number_of_smoke_detectors: number | null;
  number_of_fire_extinguishers: number | null;
  year_of_most_recent_updates_to_electrical: number | null;
  extent_of_updates_to_electrical: string;
  wiring_type: string;
  other_wiring_type: string;
  electrical_strength_amperes_a: number | null;
  electrical_panel_type: string;
  year_of_most_recent_updates_to_plumbing: number | null;
  extent_of_updates_to_plumbing: string;
  plumbing_material_type: string;
  plumbing_material_percentage: number | null;
  other_plumbing_material_type: string;
  plumbing_materials: PlumbingMaterial[];
  hot_water_tank_boiler_type: string;
  other_hot_water_tank_boiler_type: string;
  hot_water_tank_boiler_year: number | null;
  water_main_valve_shut_off_type: string;
  number_of_water_main_valve_shut_off_sensors: number | null;
  septic_tank: boolean;
  backwater_valve_type_installed: string;
  sump_pump_type: string;
  other_sump_pump_type: string;
  alarmed_sump_pump: string;
  sump_pump_auxiliary_power: string;
  other_sump_pump_auxiliary_power: string;
  water_leak_detection_system: string;
  year_of_most_recent_updates_to_roof: number | null;
  extent_of_updates_to_roof: string;
  roofing_material_type: string;
  other_roofing_material_type: string;
  impact_resistance_class_of_roofing: string;
  asphalt_roll_percentage?: number;
  asphalt_shingles_percentage?: number;
  clay_tile_percentage?: number;
  concrete_tile_percentage?: number;
  corrugated_steel_percentage?: number;
  elastomeric_percentage?: number;
  green_roof_percentage?: number;
  laminated_shingles_percentage?: number;
  metal_tile_percentage?: number;
  mineral_fibre_shakes_percentage?: number;
  rubber_percentage?: number;
  slate_tile_percentage?: number;
  tar_and_gravel_percentage?: number;
  tin_percentage?: number;
  wood_shakes_percentage?: number;
  wood_shingles_percentage?: number;
  roofing_material_type_aluminum_percentage?: number;
  roofing_material_type_copper_percentage?: number;
  roofing_material_type_plastic_percentage?: number;
  roofing_material_type_other_percentage?: number;
  aggregate_percentage?: number;
  exterior_wall_type_aluminum_percentage?: number;
  asbestos_percentage?: number;
  exterior_wall_type_brick_percentage?: number;
  brick_veneer_percentage?: number;
  cedar_siding_percentage?: number;
  cement_percentage?: number;
  cement_fibre_board_percentage?: number;
  cement_fibre_shingle_percentage?: number;
  exterior_wall_type_concrete_percentage?: number;
  exterior_wall_type_fibreglass_percentage?: number;
  log_siding_percentage?: number;
  metal_siding_percentage?: number;
  exterior_wall_type_monocoque_percentage?: number;
  exterior_wall_type_stone_percentage?: number;
  stone_veneer_percentage?: number;
  stucco_percentage?: number;
  vinyl_percentage?: number;
  wood_exterior_percentage?: number;
  exterior_wall_type_other_percentage?: number;
  year_of_most_recent_updates_to_heating: number | null;
  extent_of_updates_to_heating: string;
  primary_heating_type_installed: string;
  other_primary_heating_type: string;
  primary_heating_fuel_type: string;
  other_primary_heating_fuel_type: string;
  primary_heating_approved: boolean;
  primary_heating_approved_by: string;
  primary_heating_professionally_installed: string;
  primary_heating_fireplace_type: string;
  second_primary_heating_fuel_type: string;
  other_second_primary_heating_fuel_type: string;
  primary_heating_wett_certified: string;
  primary_heating_wett_certification_number: string;
  primary_heating_wett_inspection_date: string | null;
  chimney_type: string;
  other_chimney_type: string;
  primary_heating_fuel_type_number_of_face_cords_per_year: number | null;
  auxiliary_heating_type_installed: string;
  other_auxiliary_heating_type: string;
  auxiliary_heating_fuel_type: string;
  other_auxiliary_heating_fuel_type: string;
  auxiliary_heating_approved: boolean;
  auxiliary_heating_fuel_type_number_of_face_cords_per_year: number | null;
  auxiliary_heating_year_installed: number | null;
  auxiliary_heating_percentage_renovated: number | null;
  auxiliary_heating_automatic_fire_extinguisher: boolean;
  auxiliary_heating_approved_by: string;
  auxiliary_heating_professionally_installed: string;
  auxiliary_heating_fireplace_type: string;
  second_auxiliary_heating_fuel_type: string;
  other_second_auxiliary_heating_fuel_type: string;
  auxiliary_heating_wett_certified: string;
  auxiliary_heating_wett_certification_number: string;
  auxiliary_heating_wett_inspection_date: string | null;
  chimney_exists: string;
  chimney_built_from: string;
  chimney_built_from_other: string;
  chimney_lining: string;
  chimney_lining_other: string;
  chimney_location: string;
  chimney_location_other: string;
  chimney_professionally_cleaned: string;
  frequency_of_chimney_cleaning: string;
  chimney_certified_by: string;
  chimney_certified_by_other: string;
  wett_certified: string;
  wett_certification_number: string;
  wett_inspected: string;
  wett_inspection_report_date: string | null;
  number_of_hours_used_per_day: number | null;
  number_of_days_used_per_year: number | null;
  stove_location: string;
  stove_location_other: string;
  woodstove_installed_date: string | null;
  number_of_oil_storage_tanks: string;
  oil_tanks: OilTank[];
  claims: PropertyClaim[];
  floaters: Floater[];
  watercraft: Watercraft[];
  trailers: Trailer[];
  coverages?: LocationCoverages;
  itv_data?: ItvData | null;
  itv_locked_fields?: string[];
}

export interface ItvData {
  ConstructionFeatures?: Record<string, string | number | null>;
  Valuation?: {
    ReplacementCost?: number | null;
    CostPerSquareFoot?: number | null;
  };
  Address?: Record<string, unknown>;
  StatusCode?: string;
  panoramic_url?: string;
  [key: string]: unknown;
}

export interface EzitvResponse {
  found: boolean;
  itv_data?: ItvData;
  original_features?: Record<string, string | number | null>;
  error?: string;
}

export type ItvPrefill = Partial<
  Record<keyof Location | keyof LocationCoverages, unknown>
>;

export interface ItvUnmappedValue {
  field: string;
  value: string;
}

export interface ItvPrefillResponse {
  prefill: ItvPrefill;
  unmapped: ItvUnmappedValue[];
  locked_fields?: string[];
  error?: string;
}

export interface ItvConstructionOverrides {
  YearBuilt?: string;
  SquareFootage?: number;
  FoundationType?: string;
  ArchitecturalStyleType?: string;
  StoreyCount?: string;
  KitchenCount?: string;
  BathroomCount?: string;
  ExteriorWallType?: string;
  RoofType?: string;
  FinishedBasement?: boolean;
  GarageType?: string;
  GarageNumberOfCars?: string;
  PrimaryHeatingType?: string;
  SwimmingPoolType?: string;
  PlumbingType?: string;
  OutbuildingPresent?: string;
  SolarPanel?: string;
}

export interface ItvRecalculateRequest {
  address: EzitvAddress;
  overrides: ItvConstructionOverrides;
}

export interface ItvRecalculateResponse {
  success: boolean;
  itv_data?: ItvData;
  error?: string;
}

export interface HabConditionalRuleTrigger {
  trigger_field: string;
  show_for_codes: string[];
}

export interface HabConditionalRule {
  rules: HabConditionalRuleTrigger[];
}

export interface LocationsData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  locations: Location[];
  province: number;
  quotes_reset?: boolean;
  customer_type?: string;
}

export interface GeneralData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  locations: Location[];
}

export interface AdditionalData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  locations: Location[];
}

export interface DriverPersonalInfo {
  salutation: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  suffix: string;
  license_number: string;
  applicant_gender: string;
  marital_status: string;
  relation_to_applicant: string;
  lives_with_parents: boolean;
}

export interface OtherLicenseDetails {
  motorcycle: boolean;
  motorcycle_class: string | null;
  commercial: boolean;
  commercial_class: string | null;
  physical_impairment: boolean;
  // Dynamic motorcycle date fields
  [key: string]: string | boolean | null | undefined;
}

export interface DriverLicensing {
  date_of_birth: string | null;
  age: number | null;
  out_of_province_country: boolean;
  drivers_training: boolean;
  training_completion_date: string | null;
  other_license_details: OtherLicenseDetails;
  [key: string]:
    | string
    | number
    | boolean
    | null
    | OtherLicenseDetails
    | undefined;
}

export interface DriverInsuranceDetails {
  has_prior_insurance: boolean | null;
  continuously_insured_date: string | null;
  continuously_insured_years: number | null;
  company_insured_date: string | null;
  renewal_carriers: string[];
  current_insurer: string;
  other_insurer?: string;
  brokerage_insured_date: string | null;
  owner_principal_date: string | null;
}

export interface DriverOccupation {
  occupation: string;
  occupation_other: string;
  student_away_at_school: boolean;
  student_away_km: number | null;
  retired: boolean;
  good_student_discount: boolean;
}

export interface Membership {
  membership_group: string;
  member_number: string;
  member_date: string | null;
}

interface BaseClaim {
  id: number;
  date: string | null;
  type_code: string | null;
  fault_percentage: number | null;
  status: string;
}

export interface AutoClaim extends BaseClaim {
  source: 'dash' | 'manual';
  item_id: number | null;
  item_description: string;
  cover_types?: string[];
  cause_code: string | null;
  payout_collision: number | null;
  payout_accident_benefits: number | null;
  payout_liability_bodily_injury: number | null;
  payout_liability_property_damage: number | null;
  payout_other_pd: number | null;
  dash_coverage: string | null;
  dash_claim_number?: string | null;
  dash_loss_description?: string | null;
  dash_coverage_description?: string | null;
}

export interface PropertyClaim extends BaseClaim {
  source: 'manual';
  cause_code: string | null;
  payout_amount: number | null;
}

export type Claim = AutoClaim | PropertyClaim;

export interface Conviction {
  id: number;
  source: 'mvr' | 'dash' | 'manual';
  date: string | null;
  conviction_code: string | null;
  conviction_code_other: string | null;
  speed_over_limit_kmh: number | null;
  mvr_code?: string | null;
  mvr_description?: string | null;
  mvr_offence_date?: string | null;
}

export interface Suspension {
  id: number;
  source: 'mvr' | 'dash' | 'manual';
  start_date: string | null;
  end_date: string | null;
  reason: string;
  reason_code: string | null;
  reason_other: string | null;
  mvr_description?: string | null;
}

export interface Lapse {
  id: number;
  source: 'dash' | 'manual';
  reason: string;
  reason_code: string | null;
  reason_other: string | null;
  lapse_date: string | null;
  reinstatement_date: string | null;
  duration_months: number | null;
}

export interface FraudMisrepresentation {
  id: number;
  source: 'dash' | 'manual';
  date: string | null;
  reason_code: string | null;
  reason_other: string | null;
  policy_status_code: string | null;
}

export interface DriverIncidents {
  claims: AutoClaim[];
  convictions: Conviction[];
  suspensions: Suspension[];
  lapses: Lapse[];
  fraud_misrepresentation: FraudMisrepresentation[];
}

export interface Driver {
  id: number;
  excluded: boolean;
  dash_driver_index: number | null;
  personal_info: DriverPersonalInfo;
  licensing: DriverLicensing;
  insurance_details: DriverInsuranceDetails;
  occupation: DriverOccupation;
  memberships: Membership[];
  incidents: DriverIncidents;
}

export interface DriversData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  effective_date: string;
  drivers: Driver[];
  province: number;
  quotes_reset?: boolean;
}

export const apiUpdateAutoQuoteDrivers = (
  id: number,
  drivers: Driver[],
): Promise<DriversData> => {
  return PATCH(`api/quote-marketplace/auto/quotes/${id}/drivers/`, {
    drivers,
  });
};

export interface AssignmentDriver {
  id: number;
  excluded: boolean;
  first_name: string;
  last_name: string;
  license_class: string;
  is_learner: boolean;
}

export interface AssignmentVehicle {
  id: number;
  vehicle_model_year: number | null;
  make: string;
  model: string;
  vin: string;
  registered_owner_name: string;
  principal_driver_id: number | null;
  secondary_drivers: number[];
  occasional_drivers: number[];
  learner_drivers: number[];
  telematics_enabled: boolean;
}

export interface AssignmentData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  province: number;
  vehicles: AssignmentVehicle[];
  drivers: AssignmentDriver[];
}

export const apiUpdateAutoQuoteVehicles = (
  id: number,
  vehicles: Vehicle[],
): Promise<VehiclesData> => {
  return PATCH(`api/quote-marketplace/auto/quotes/${id}/vehicles/`, {
    vehicles,
  });
};

export const apiUpdatePropertyQuoteLocations = (
  id: number,
  locations: Location[],
): Promise<LocationsData> => {
  return PATCH(`api/quote-marketplace/property/quotes/${id}/locations/`, {
    locations,
  });
};

export interface EzitvAddress {
  street_address: string;
  city: string;
  postal_code: string;
  province: string;
}

export const apiFetchPropertyEzitv = (
  quoteId: number,
  address: EzitvAddress,
): Promise<EzitvResponse> => {
  return POST(
    `api/quote-marketplace/property/quotes/${quoteId}/evaluation-tools/ezitv/`,
    address,
  );
};

export const apiFetchItvPrefill = (
  quoteId: number,
  itvData: ItvData,
): Promise<ItvPrefillResponse> => {
  return POST(
    `api/quote-marketplace/property/quotes/${quoteId}/evaluation-tools/itv-prefill/`,
    { itv_data: itvData },
  );
};

export const apiRecalculateItv = (
  quoteId: number,
  request: ItvRecalculateRequest,
): Promise<ItvRecalculateResponse> => {
  return POST(
    `api/quote-marketplace/property/quotes/${quoteId}/evaluation-tools/itv-recalculate/`,
    request,
  );
};

export const apiDeleteItvRecalculation = (
  quoteId: number,
): Promise<{ success: boolean; deleted: boolean }> => {
  return DELETE(
    `api/quote-marketplace/property/quotes/${quoteId}/evaluation-tools/itv-recalculate/`,
  );
};

export const apiStartQuoteVinLookup = (
  quoteId: number,
  vin: string,
): Promise<VinLookupResult> => {
  return POST(`api/quote-marketplace/auto/quotes/${quoteId}/vin-lookup/`, {
    vin,
  });
};

export const apiGetQuoteVinLookup = (
  quoteId: number,
  vin: string,
): Promise<VinLookupResult> => {
  return GET(
    `api/quote-marketplace/auto/quotes/${quoteId}/vin-lookup/?vin=${encodeURIComponent(vin)}`,
  );
};

export interface ViccLookupResult {
  vicc_code?: string;
  status?: 'pending' | 'found' | 'not_found' | 'error' | 'none';
  found?: boolean;
  error?: string;
  make?: string;
  model?: string;
  fuel_type?: string;
  available_years?: string[];
  multiple_results?: boolean;
}

export const apiStartQuoteViccLookup = (
  quoteId: number,
  vicc_code: string,
): Promise<ViccLookupResult> => {
  return POST(`api/quote-marketplace/auto/quotes/${quoteId}/vicc-lookup/`, {
    vicc_code,
  });
};

export const apiGetQuoteViccLookup = (
  quoteId: number,
  vicc_code: string,
): Promise<ViccLookupResult> => {
  return GET(
    `api/quote-marketplace/auto/quotes/${quoteId}/vicc-lookup/?vicc_code=${encodeURIComponent(vicc_code)}`,
  );
};

export interface VehicleManufacturer {
  ref: string;
  description: string;
}

export interface VehicleModel {
  ref: string;
  description: string;
}

export interface VehicleYear {
  year: string;
  vehicle_ref: string;
  fuel_type: string;
}

export const apiGetVehicleManufacturers = (): Promise<{
  manufacturers: VehicleManufacturer[];
}> => {
  return GET('api/quote-marketplace/auto/vehicle-manufacturers/');
};

export const apiGetVehicleModels = (
  manufacturerRef: string,
): Promise<{ models: VehicleModel[] }> => {
  return GET(
    `api/quote-marketplace/auto/vehicle-models/?manufacturer_ref=${encodeURIComponent(manufacturerRef)}`,
  );
};

export const apiGetVehicleYears = (
  modelRef: string,
): Promise<{ years: VehicleYear[] }> => {
  return GET(
    `api/quote-marketplace/auto/vehicle-years/?model_ref=${encodeURIComponent(modelRef)}`,
  );
};

export interface PostalCodeLookupResult {
  found: boolean;
  error?: string;
  postal_code?: string;
  cities?: string[];
}

export const apiPostalCodeLookup = (
  postalCode: string,
): Promise<PostalCodeLookupResult> => {
  const encoded = encodeURIComponent(postalCode.trim().toUpperCase());
  return GET(`api/quote-marketplace/postal-code/${encoded}/`);
};

export interface PopulateFieldsResult {
  success: boolean;
  vehicles_added: number;
  drivers_added: number;
  claims_found: number;
  vehicles: Vehicle[];
  drivers: Driver[];
  message: string;
  error?: string;
}

export const apiPopulateFields = (
  quoteId: number,
  licenseNumber: string,
): Promise<PopulateFieldsResult> => {
  return POST(`api/quote-marketplace/quotes/${quoteId}/populate-fields/`, {
    license_number: licenseNumber,
  });
};

export interface ValidationError {
  field: string;
  message: string;
  tab: string;
  section: string | null;
  driver_id: number | null;
  vehicle_id: number | null;
  required_by_markets?: string[] | null;
}

export interface AvailableCarrier {
  carrier_id: number;
  name: string;
  product: string;
  is_telematics: boolean;
  display_name: string;
}

export interface RatingNote {
  type: string;
  amount: number | null;
  description: string;
}

export interface TaxFee {
  amount: number;
  type: string;
  currency?: string;
  ref?: string;
  desc?: string;
  percentage_amount?: number;
}

export interface RatedVehicleCoverages {
  vin?: string;
  liability?: {
    limit_cad?: string;
    bodily_injury?: { limit_cad?: number };
    property_damage?: { limit_cad?: number };
  };
  all_perils?: {
    coverage?: string;
    deductible_cad?: string;
    deductible_other_cad?: number;
  };
  collision_or_upset?: {
    coverage?: string;
    deductible_cad?: string;
    deductible_other_cad?: number;
  };
  comprehensive?: {
    coverage?: string;
    deductible_cad?: string;
    deductible_other_cad?: number;
  };
  dcpd?: {
    deductible_cad?: string;
  };
  accident_benefits?: {
    coverage?: string;
  };
  increased_accident_benefits_income_replacement?: {
    limit_per_week_cad?: string;
  };
  increased_accident_benefits_medical_rehabilitation_and_attendant_care?: {
    catastrophic_impairment?: { limit_cad?: string };
    non_catastrophic_impairment?: { limit_cad?: string };
  };
  uninsured_automobile?: {
    coverage?: string;
  };
  discounts?: string[];
  [key: string]: unknown;
}

export interface RatedLocationCoverages {
  discounts?: string[];
  [key: string]: unknown;
}

export interface RatedCoverages {
  vehicles?: RatedVehicleCoverages[];
  locations?: RatedLocationCoverages[];
  liability?: Record<string, unknown>;
}

export interface CarrierQuote {
  id: number;
  carrier_id: number;
  carrier_name: string;
  acturis_product: string | null;
  status: 'pending' | 'quoted' | 'declined' | 'error' | 'timed_out';
  raw_status: string | null;
  annual_premium: string | null;
  monthly_premium: string | null;
  taxes: TaxFee[] | null;
  fees: TaxFee[] | null;
  decline_reason: string | null;
  error_message: string | null;
  is_credential_error: boolean;
  rating_notes: RatingNote[] | null;
  rated_coverages: RatedCoverages | null;
  location_premiums: (string | null)[] | null;
  vehicle_premiums: (string | null)[] | null;
  acturis_quote_ref: string | null;
  valid_until_date: string | null;
}

export interface CarriersVehicle {
  id: number;
  vehicle_model_year: number | null;
  make: string;
  model: string;
  vin: string;
  coverages: VehicleCoverages;
  telematics_enabled: boolean;
  winter_tires: boolean;
  single_vehicle_mvd: boolean;
  policy_discount: 'combined' | 'multi_line' | 'disabled';
}

export interface CarriersLocationCoverages {
  coverage_form_type: string;
  deductible_cad: number | null;
  liability_limit_cad: number | null;
  grbc_enabled: boolean;
  single_limit_enabled: boolean;
  sewer_backup_enabled: boolean;
  sewer_backup_limit_cad: number | null;
  sewer_backup_deductible_cad: number | null;
  ground_water_enabled: boolean;
  ground_water_limit_cad: number | null;
  ground_water_deductible_cad: number | null;
  above_ground_water_enabled: boolean;
  above_ground_water_limit_cad: number | null;
  above_ground_water_deductible_cad: number | null;
  overland_water_enabled: boolean;
  overland_water_limit_cad: number | null;
  overland_water_deductible_cad: number | null;
  service_line_enabled: boolean;
  service_line_limit_cad: number | null;
  service_line_deductible_cad: number | null;
  bylaw_enabled: boolean;
  bylaw_limit_cad: number | null;
  bylaw_deductible_cad: number | null;
  fire_department_enabled: boolean;
  fire_department_limit_cad: number | null;
  fire_department_deductible_cad: number | null;
  identity_theft_enabled: boolean;
  identity_theft_limit_cad: number | null;
  identity_theft_deductible_cad: number | null;
}

export interface CarriersLocation {
  id: number;
  location_type: string;
  address: string;
  city: string;
  province: string;
  postal_code: string;
  building_coverage: number | null;
  outbuildings: number | null;
  contents: number | null;
  coverages: CarriersLocationCoverages;
  burglar_alarm: string;
  fire_alarm: string;
  number_of_mortgages: number | null;
  claims_count: number;
  sump_pump_type: string;
}

export type RfqStatus =
  | 'not_started'
  | 'shell_created'
  | 'submitted'
  | 'polling'
  | 'complete'
  | 'error';

export interface RfqError {
  message: string | null;
  response: Record<string, unknown> | null;
}

export type RiskDetailsSyncStatus = 'idle' | 'pending' | 'synced' | 'error';

export interface RiskDetailsSync {
  status: RiskDetailsSyncStatus;
  error: RfqError | null;
  pending_seconds: number | null;
  queued: boolean;
}

// Unsaved step changes, keyed by step, each in the body its save sends
export type RiskDetailsDraftSteps = Record<string, unknown>;

export const apiSendRiskDetailsDraft = (
  quoteId: number,
  steps: RiskDetailsDraftSteps,
) => {
  return POST(`api/quote-marketplace/quotes/${quoteId}/risk-details-draft/`, {
    steps,
  });
};

interface BaseCarriersData {
  id: number;
  lock: QuoteLock;
  province: number;
  is_valid_for_rfq: boolean;
  validation_errors: ValidationError[];
  available_carriers: AvailableCarrier[];
  rfq_status: RfqStatus;
  rfq_error: RfqError | null;
  risk_details_sync: RiskDetailsSync;
  carrier_quotes: CarrierQuote[];
  selected_markets: string[];
  selected_carrier_quote_id: number | null;
}

export interface AutoCarriersData extends BaseCarriersData {
  line_of_business: typeof LOB_PERSONAL_AUTO;
  vehicles: CarriersVehicle[];
  has_reports_consent: boolean;
  driver_reports_status: string | null;
}

export interface CarriersInsuranceHistory {
  multi_policy_discount: boolean;
  multi_property_discount: boolean;
  membership_discount_membership_group: string;
}

export interface PropertyCarriersData extends BaseCarriersData {
  line_of_business: typeof LOB_PERSONAL_PROPERTY;
  locations: CarriersLocation[];
  insurance_history: CarriersInsuranceHistory;
}

export type CarriersData = AutoCarriersData | PropertyCarriersData;

export const apiSubmitAutoQuoteRfq = (id: number): Promise<CarriersData> => {
  return POST(`api/quote-marketplace/auto/quotes/${id}/carriers/`, {});
};

export const apiSubmitPropertyQuoteRfq = (
  id: number,
): Promise<CarriersData> => {
  return POST(`api/quote-marketplace/property/quotes/${id}/carriers/`, {});
};

export const apiUpdateSelectedMarkets = (
  id: number,
  selectedMarkets: string[],
  lineOfBusiness: 'auto' | 'property' = 'auto',
): Promise<CarriersData> => {
  return PATCH(
    `api/quote-marketplace/${lineOfBusiness}/quotes/${id}/carriers/`,
    { selected_markets: selectedMarkets },
  );
};

export const apiSelectCarrierQuote = (
  quoteId: number,
  carrierQuoteId: number,
  lineOfBusiness: 'auto' | 'property' = 'auto',
): Promise<CarriersData> => {
  return POST(
    `api/quote-marketplace/${lineOfBusiness}/quotes/${quoteId}/carriers/${carrierQuoteId}/select/`,
    {},
  );
};

export const apiUpdateVehicleCoverages = (
  quoteId: number,
  vehicleId: number,
  coverages: VehicleCoverages,
  lineOfBusiness: 'auto' | 'property' = 'auto',
): Promise<CarriersData> => {
  return PATCH(
    `api/quote-marketplace/${lineOfBusiness}/quotes/${quoteId}/vehicles/${vehicleId}/coverages/`,
    { coverages },
  );
};

export const apiUpdateLocationCoverages = (
  quoteId: number,
  locationId: number,
  coverages: LocationCoverages,
): Promise<CarriersData> => {
  return PATCH(
    `api/quote-marketplace/property/quotes/${quoteId}/locations/${locationId}/coverages/`,
    { coverages },
  );
};

export interface AnswerCodeOption {
  code: string;
  label: string;
  available_for_types?: string[];
}

export interface AnswerCodesData {
  // Driver/applicant fields
  applicant_gender: AnswerCodeOption[];
  marital_status: AnswerCodeOption[];
  occupation: AnswerCodeOption[];
  relation_to_applicant: AnswerCodeOption[];

  // Driver insurance fields
  current_insurer: AnswerCodeOption[];
  current_previous_insurer: AnswerCodeOption[];
  previously_insured: AnswerCodeOption[];

  // Vehicle fields
  fuel_type: AnswerCodeOption[];
  vehicle_body_type: AnswerCodeOption[];
  garaging: AnswerCodeOption[];
  vehicle_use: AnswerCodeOption[];
  vehicle_ownership: AnswerCodeOption[];
  condition_when_purchased_leased: AnswerCodeOption[];
  antitheft_device_type: AnswerCodeOption[];
  antitheft_product: AnswerCodeOption[];
  vehicle_safety_features: AnswerCodeOption[];
  vehicle_compatible_with_telematics_program: AnswerCodeOption[];

  // Property policy fields
  habitational_policy_type: AnswerCodeOption[];
  current_habitational_insurer: AnswerCodeOption[];

  // Location fields (habitational)
  location_type?: AnswerCodeOption[];
  location_form_type?: AnswerCodeOption[];
  metres_to_hydrant?: AnswerCodeOption[];
  hab_occupation?: AnswerCodeOption[];
  construction_type?: AnswerCodeOption[];
  exterior_wall_type?: AnswerCodeOption[];
  property_type?: AnswerCodeOption[];
  garage_type?: AnswerCodeOption[];
  number_of_storeys?: AnswerCodeOption[];
  number_of_kitchens?: AnswerCodeOption[];
  building_shape?: AnswerCodeOption[];
  building_foundation_type?: AnswerCodeOption[];
  building_foundation_material_type?: AnswerCodeOption[];
  occupancy?: AnswerCodeOption[];
  occupants_relation_to_insured?: AnswerCodeOption[];
  resident_smokers?: AnswerCodeOption[];
  first_time_homebuyer?: AnswerCodeOption[];
  burglar_alarm?: AnswerCodeOption[];
  fire_alarm?: AnswerCodeOption[];
  dwelling_use?: AnswerCodeOption[];
  location_accessibility?: AnswerCodeOption[];
  breeds_of_dogs?: AnswerCodeOption[];

  // Tenant/Condo specific fields (habitational)
  tenant_condo_building_foundation_type?: AnswerCodeOption[];
  tenant_condo_exterior_wall_material_type?: AnswerCodeOption[];
  tenant_condo_roofing_material_type?: AnswerCodeOption[];

  // Electrical fields (habitational)
  wiring_type?: AnswerCodeOption[];
  electrical_panel_type?: AnswerCodeOption[];
  extent_of_updates_to_electrical?: AnswerCodeOption[];

  // Plumbing fields (habitational)
  plumbing_material_type?: AnswerCodeOption[];
  hot_water_tank_boiler_type?: AnswerCodeOption[];
  water_main_valve_shut_off_type?: AnswerCodeOption[];
  extent_of_updates_to_plumbing?: AnswerCodeOption[];
  backwater_valve_type_installed?: AnswerCodeOption[];
  sump_pump_type?: AnswerCodeOption[];
  alarmed_sump_pump?: AnswerCodeOption[];
  sump_pump_auxiliary_power?: AnswerCodeOption[];
  water_leak_detection_system?: AnswerCodeOption[];

  // Roofing fields (habitational)
  roofing_material_type?: AnswerCodeOption[];
  impact_resistance_class_of_roofing?: AnswerCodeOption[];
  extent_of_updates_to_roof?: AnswerCodeOption[];

  // Heating fields (habitational)
  primary_heating_type_installed?: AnswerCodeOption[];
  primary_heating_fuel_type?: AnswerCodeOption[];
  primary_heating_approved_by?: AnswerCodeOption[];
  primary_heating_professionally_installed?: AnswerCodeOption[];
  primary_heating_fireplace_type?: AnswerCodeOption[];
  second_primary_heating_fuel_type?: AnswerCodeOption[];
  primary_heating_wett_certified?: AnswerCodeOption[];
  chimney_type?: AnswerCodeOption[];
  chimney_exists?: AnswerCodeOption[];
  chimney_built_from?: AnswerCodeOption[];
  chimney_lining?: AnswerCodeOption[];
  chimney_location?: AnswerCodeOption[];
  chimney_professionally_cleaned?: AnswerCodeOption[];
  frequency_of_chimney_cleaning?: AnswerCodeOption[];
  stove_location?: AnswerCodeOption[];
  extent_of_updates_to_heating?: AnswerCodeOption[];
  auxiliary_heating_type_installed?: AnswerCodeOption[];
  auxiliary_heating_fuel_type?: AnswerCodeOption[];
  auxiliary_heating_approved_by?: AnswerCodeOption[];
  auxiliary_heating_professionally_installed?: AnswerCodeOption[];
  auxiliary_heating_fireplace_type?: AnswerCodeOption[];
  second_auxiliary_heating_fuel_type?: AnswerCodeOption[];
  auxiliary_heating_wett_certified?: AnswerCodeOption[];

  // Oil tank fields (habitational)
  number_of_oil_storage_tanks?: AnswerCodeOption[];
  oil_storage_tank_construction_type?: AnswerCodeOption[];
  oil_storage_tank_location?: AnswerCodeOption[];
  oil_storage_tank_heating_associated_with?: AnswerCodeOption[];

  // Credit consent fields (habitational)
  credit_consent: AnswerCodeOption[];
  privacy_consent: AnswerCodeOption[];
  relation_to_insured: AnswerCodeOption[];
  has_applicant_changed_address_in_the_last_3_years: AnswerCodeOption[];

  // Habitational insurance history fields
  hab_current_habitational_insurer?: AnswerCodeOption[];
  hab_current_auto_insurer?: AnswerCodeOption[];
  hab_multi_policy_discount?: AnswerCodeOption[];
  hab_membership_discount_membership_group?: AnswerCodeOption[];
  hab_additional_membership_discount_membership_group?: AnswerCodeOption[];
  hab_any_policy_cancelled_declined_or_renewal_rejected_by_any_insurer?: AnswerCodeOption[];
  hab_current_previous_insurer?: AnswerCodeOption[];
  hab_policy_cancelled_or_declined?: AnswerCodeOption[];
  hab_reason_for_decline_cancellation?: AnswerCodeOption[];

  // Home based business fields (habitational)
  business_description?: AnswerCodeOption[];

  earthquake_coverage_option?: AnswerCodeOption[];
  solar_panel_coverage_type?: AnswerCodeOption[];
  solar_panel_settlement_basis?: AnswerCodeOption[];
  swimming_pool_material?: AnswerCodeOption[];
  property_in_storage_coverage_type?: AnswerCodeOption[];

  membership_discount_membership_group: AnswerCodeOption[];
  additional_membership_discount_membership_group?: AnswerCodeOption[];

  // Policy questions
  any_policy_cancelled_declined_or_renewal_rejected_by_any_insurer: AnswerCodeOption[];

  // Coverage fields (deductibles and limits)
  liability_limit_cad: AnswerCodeOption[];
  dcpd_deductible_cad: AnswerCodeOption[];
  all_perils_deductible_cad: AnswerCodeOption[];
  collision_deductible_cad: AnswerCodeOption[];
  comprehensive_deductible_cad: AnswerCodeOption[];

  // Accident benefits fields
  income_replacement_limit_per_week_cad: AnswerCodeOption[];
  catastrophic_impairment_limit_cad: AnswerCodeOption[];
  non_catastrophic_impairment_limit_cad: AnswerCodeOption[];
  caregiver_coverage_options: AnswerCodeOption[];
  housekeeping_home_maintenance_coverage_options: AnswerCodeOption[];

  // Driver incident fields
  conviction_code: AnswerCodeOption[];
  suspension_reason: AnswerCodeOption[];
  lapse_reason_applicant: AnswerCodeOption[];
  lapse_reason_non_applicant: AnswerCodeOption[];
  cancel_decline_reason_applicant: AnswerCodeOption[];
  cancel_decline_reason_non_applicant: AnswerCodeOption[];

  claim_type: AnswerCodeOption[];
  claim_cause: AnswerCodeOption[];
  claim_cover_type: AnswerCodeOption[];

  floater_item_type?: AnswerCodeOption[];
  floater_coverage_required?: AnswerCodeOption[];
  floater_item_exhibited?: AnswerCodeOption[];

  watercraft_propulsion_type?: AnswerCodeOption[];
  watercraft_mooring_location?: AnswerCodeOption[];
  watercraft_watercraft_usage?: AnswerCodeOption[];
  watercraft_waters_navigated?: AnswerCodeOption[];

  trailer_condition_when_purchased?: AnswerCodeOption[];
  trailer_trailer_type?: AnswerCodeOption[];
  trailer_is_the_trailer_road_licensed?: AnswerCodeOption[];
  trailer_province_territory?: AnswerCodeOption[];
  trailer_occupied_by_the_owner?: AnswerCodeOption[];
  trailer_the_trailer_is_permanently_parked?: AnswerCodeOption[];
  trailer_hail_coverage_required?: AnswerCodeOption[];

  conditional_rules?: {
    speed_over_limit_kmh?: {
      depends_on: string;
      show_for_codes: string[];
    };
    conviction_code_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
    suspension_reason_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
    lapse_reason_other_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    lapse_reason_other_non_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    cancel_decline_reason_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    cancel_decline_reason_non_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    cancel_decline_reason_other_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    cancel_decline_reason_other_non_applicant?: {
      depends_on: string;
      show_for_codes: string[];
    };
    antitheft_device_type?: {
      depends_on: string;
      show_for_codes: string[];
    };
    antitheft_product?: {
      depends_on: string;
      show_for_codes: string[];
    };
    antitheft_device_type_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
    vehicle_safety_features_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
    income_replacement_limit_per_week_cad_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
    occupation_other?: {
      depends_on: string;
      show_for_codes: string[];
    };
  };

  hab_conditional_rules?: {
    [field: string]: HabConditionalRule;
  };

  hab_conditional_field_names?: string[];
  hab_occupation_other?: {
    depends_on: string;
    show_for_codes: string[];
  };
  heating_fuel_mapping?: {
    [heatingTypeCode: string]: string[];
  };

  applicant_relation_code?: string;
  fraud_policy_status_applicant?: AnswerCodeOption[];
  fraud_policy_status_non_applicant?: AnswerCodeOption[];

  license_config?: {
    [province: string]: {
      class_question: string;
      classes: AnswerCodeOption[];
      date_fields: {
        field: string;
        label: string;
        acturis_name: string;
        allowed_class_codes: string[];
      }[];
      motorcycle: {
        class_question: string;
        classes: AnswerCodeOption[];
        date_fields: {
          field: string;
          label: string;
          acturis_name: string;
          allowed_class_codes: string[];
        }[];
      };
      commercial_classes: AnswerCodeOption[];
    };
  };

  synced_at: string | null;
  version_ref?: string;
  effective_date?: string;
  effective_date_range?: {
    min_date: string;
    max_date: string;
  };
  warning?: {
    code: string;
    message: string;
    requested_date: string;
    valid_from: string | null;
    valid_to: string | null;
  };
}

export const apiGetAnswerCodes = (
  effectiveDate?: string | null,
  productTarget?: 'auto' | 'habitational' | null,
): Promise<AnswerCodesData> => {
  const params = new URLSearchParams();
  if (effectiveDate) {
    params.append('effective_date', effectiveDate);
  }
  if (productTarget) {
    params.append('product_target', productTarget);
  }
  const queryString = params.toString();
  return GET(
    `api/acturis-customers/answer-codes/${queryString ? `?${queryString}` : ''}`,
  );
};

export interface MandatoryFieldsData {
  policy?: string[];
  driver?: string[];
  vehicle?: string[];
  'vehicle/garaging_address'?: string[];
  'vehicle/liability'?: string[];
  'vehicle/collision_or_upset'?: string[];
  'vehicle/comprehensive'?: string[];
  'vehicle/all_perils'?: string[];
  _meta: {
    synced_at: string | null;
    version: string;
  };
}

export const apiGetMandatoryFields = (): Promise<MandatoryFieldsData> => {
  return GET('api/acturis-customers/mandatory-fields/');
};

export type ConditionType =
  | 'always'
  | 'when'
  | 'any_of'
  | 'when_any_of'
  | 'when_item_enabled'
  | 'when_and'
  | 'when_or';
export type OperatorType =
  | 'equals'
  | 'not_equals'
  | 'in'
  | 'not_in'
  | 'not_empty'
  | 'greater_than';

export interface FieldCondition {
  condition: ConditionType;
  field?: string;
  operator?: OperatorType;
  value?: unknown;
  fields?: string[];
  message?: string;
  conditions?: FieldCondition[];
}

export interface VisibleWhen {
  field: string;
  operator: string;
  values: string[];
}

export interface FieldRule {
  required: boolean | FieldCondition;
  type: string;
  tab: string;
  section: string;
  message: string;
  format?: string;
  format_message?: string;
  allowed_values?: string[];
  min_length?: number;
  min_value?: number;
  visible_when?: VisibleWhen;
}

export interface CrossFieldRuleCondition {
  type: 'min_count' | 'any_of' | 'all_of';
  collection?: string;
  filter?: Record<string, unknown>;
  min?: number;
  rules?: Array<{
    field: string;
    operator: string;
    value?: unknown;
    and?: {
      field: string;
      operator: string;
      value?: unknown;
    };
  }>;
}

export interface CrossFieldRule {
  id: string;
  message: string;
  tab: string;
  condition: CrossFieldRuleCondition;
}

export type ItemPath =
  | 'driver'
  | 'vehicle'
  | 'policy'
  | 'vehicle/liability'
  | 'vehicle/collision_or_upset'
  | 'vehicle/comprehensive'
  | 'vehicle/all_perils'
  | 'vehicle/dcpd'
  | 'vehicle/garaging_address'
  | 'driver/claim'
  | 'driver/conviction'
  | 'driver/suspension'
  | 'driver/lapse'
  | 'driver/fraud_misrepresentation'
  | 'location'
  | 'location/coverages'
  | 'location/claim'
  | 'location/floater'
  | 'location/watercraft'
  | 'location/oil_tank'
  | 'watercraft'
  | 'trailer'
  | 'liability'
  | 'prior_address_history'
  | 'prior_insurance_history';

export type ValidationProductTarget = 'auto' | 'habitational';

export interface MarketFieldFollowsRule {
  field: string;
  values: string[];
}

export interface MarketFieldRule {
  acturis_path: string;
  required_by: string[];
  display_names: string[];
  message: string;
  follows?: MarketFieldFollowsRule;
}

export interface ValidationSchema {
  version: string | null;
  generated_at: string;
  effective_date: string | null;
  product_target: ValidationProductTarget;
  fields: Partial<Record<ItemPath, Record<string, FieldRule>>>;
  cross_field_rules: CrossFieldRule[];
  market_fields?: Partial<Record<ItemPath, Record<string, MarketFieldRule>>>;
  selected_markets?: string[];
}

export const apiGetValidationSchema = (
  quoteId: number,
): Promise<ValidationSchema> => {
  return GET(`api/quote-marketplace/validation-schema/?quote_id=${quoteId}`);
};

export type DriverReportsStatus =
  | 'not_started'
  | 'in_progress'
  | 'complete'
  | 'error';

export type DriverMVRStatusType =
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'no_match'
  | 'pending';

export interface DriverMVRStatus {
  driver_id: number;
  driver_name: string;
  license_number: string;
  status: DriverMVRStatusType;
  error_message: string | null;
  is_retryable: boolean;
}

export type MVRErrorType =
  | 'timeout'
  | 'service_unavailable'
  | 'partial_failure'
  | 'all_failed';

export interface MVRErrorDetails {
  error_type: MVRErrorType;
  is_retryable: boolean;
  summary_message: string;
  driver_statuses: DriverMVRStatus[];
}

export interface Discrepancy {
  type: string;
  driver_id: number;
  driver_name: string;
  source: 'mvr' | 'dash';
  field: string;
  current_value: unknown;
  report_value: unknown;
  tab: string;
  section: string;
  index: number;
}

export type DriverReportsDecision =
  | 'skipped'
  | 'applied'
  | 'proceeded_without_mvr'
  | null;

export interface DriverReportsResponse {
  status: DriverReportsStatus;
  has_discrepancies?: boolean;
  discrepancies?: Discrepancy[];
  mvr_order_id?: number;
  error?: string;
  viewed_indices?: number[];
  decision?: DriverReportsDecision;
  can_retry?: boolean;
  can_proceed_without_mvr?: boolean;
  error_details?: MVRErrorDetails;
  successful_driver_count?: number;
  failed_driver_count?: number;
}

export interface ProceedWithoutMVRResponse {
  status: 'proceeded_without_mvr';
  decision: 'proceeded_without_mvr';
}

export interface MarkViewedResponse {
  viewed_indices: number[];
  remaining_count: number;
  all_viewed: boolean;
}

export interface ApplyChangesResponse {
  status: 'changes_applied';
  changes_applied: Array<{
    driver_id: number;
    field: string;
    source: 'mvr' | 'dash';
    count: number;
  }>;
  redirect_to?: string;
}

export interface SkipChangesResponse {
  status: 'skipped';
}

const driverReportsUrl = (quoteId: number) =>
  `api/quote-marketplace/auto/quotes/${quoteId}/driver-reports/`;

export const apiStartDriverReports = (
  quoteId: number,
): Promise<DriverReportsResponse> => POST(driverReportsUrl(quoteId), {});

export const apiGetDriverReports = (
  quoteId: number,
): Promise<DriverReportsResponse> => GET(driverReportsUrl(quoteId));

export const apiApplyDriverReportsChanges = (
  quoteId: number,
): Promise<ApplyChangesResponse> =>
  POST(driverReportsUrl(quoteId), { action: 'apply_changes' });

export const apiSkipDriverReportsChanges = (
  quoteId: number,
): Promise<SkipChangesResponse> =>
  POST(driverReportsUrl(quoteId), { action: 'skip_changes' });

export const apiMarkDiscrepancyViewed = (
  quoteId: number,
  index: number,
): Promise<MarkViewedResponse> =>
  POST(driverReportsUrl(quoteId), { action: 'mark_viewed', index });

export const apiProceedWithoutMVR = (
  quoteId: number,
): Promise<ProceedWithoutMVRResponse> =>
  POST(driverReportsUrl(quoteId), { action: 'proceed_without_mvr' });

export interface SelectedCarrierInfo {
  id: number;
  carrier_id: number;
  carrier_name: string;
  acturis_quote_ref: string | null;
  annual_premium: string | null;
}

export interface MvrReportData {
  driver_name: string;
  license_number: string;
  mvr_order_id: number;
  mvr_request_id: number;
  pdf_available: boolean;
}

export interface DashReportRef {
  driver_name: string;
  license_number: string;
  dash_log_id: number | null;
  status: 'not_ordered' | 'ordered' | 'available' | 'none';
  is_full_history: boolean;
}

interface BaseValidationsData {
  id: number;
  lock: QuoteLock;
  selected_carrier: SelectedCarrierInfo | null;
  discrepancies_available: boolean;
  discrepancies: Discrepancy[];
  viewed_indices: number[];
  decision: DriverReportsDecision;
}

export interface AutoValidationsData extends BaseValidationsData {
  line_of_business: typeof LOB_PERSONAL_AUTO;
  reports_available: boolean;
  mvr_reports: MvrReportData[];
  dash_reports: DashReportRef[];
}

export interface PropertyValidationsData {
  id: number;
  line_of_business: typeof LOB_PERSONAL_PROPERTY;
  lock: QuoteLock;
  selected_carrier: SelectedCarrierInfo | null;
}

export type ValidationsData = AutoValidationsData | PropertyValidationsData;

export interface RatingFactor {
  label: string;
  value: string;
}

export interface QuoteSummaryRiskInfo {
  vrg_effective_date: string | null;
  rating_factors: RatingFactor[] | null;
}

export interface QuoteSummarySelectedCarrier {
  id: number;
  carrier_id: number;
  carrier_name: string;
  acturis_quote_ref: string | null;
  valid_until_date: string | null;
  annual_premium: string | null;
  monthly_premium: string | null;
  taxes: TaxFee[] | null;
  fees: TaxFee[] | null;
  rating_notes: RatingNote[] | null;
  rated_coverages: RatedCoverages | null;
  risk_info: QuoteSummaryRiskInfo | null;
  quote_details_html: string | null;
  coverage_breakdown_html: string | null;
  discounts_html: string | null;
  rating_factors_html: string | null;
}

export interface QuoteSummaryVehicle {
  id: number;
  vehicle_model_year: number | null;
  make: string;
  model: string;
  vin: string;
  coverages: VehicleCoverages;
  messages: string[];
  discounts?: string[];
}

export interface QuoteSummaryData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  effective_date: string;
  province: number;
  selected_carrier: QuoteSummarySelectedCarrier | null;
  vehicles: QuoteSummaryVehicle[];
  group_id: number | null;
}

export interface PropertyQuoteSummarySelectedCarrier {
  id: number;
  carrier_id: number;
  carrier_name: string;
  acturis_quote_ref: string | null;
  valid_until_date: string | null;
  annual_premium: string | null;
  policy_premium: string | null;
  monthly_premium: string | null;
  taxes: TaxFee[] | null;
  fees: TaxFee[] | null;
  rating_notes: RatingNote[] | null;
  rated_coverages: RatedCoverages | null;
  quote_details_html: string | null;
  policy_coverage_html: string | null;
  coverage_breakdown_html: string | null;
  discounts_html: string | null;
}

export interface QuoteSummaryLocation {
  id: number;
  location_type: string;
  address: string;
  city: string;
  province: string;
  postal_code: string;
  building_coverage: number | null;
  outbuildings: number | null;
  contents: number | null;
  coverages: LocationCoverages;
  messages: string[];
  discounts?: string[];
}

export interface PropertyQuoteSummaryData {
  id: number;
  line_of_business: number;
  lock: QuoteLock;
  effective_date: string;
  province: number;
  selected_carrier: PropertyQuoteSummarySelectedCarrier | null;
  locations: QuoteSummaryLocation[];
  group_id: number | null;
}

export interface MarketCredential {
  carrier_id: number;
  name: string;
  provider_code: string;
  provider_name: string;
  has_credentials: boolean;
  is_selected_for_rfq: boolean;
  username: string | null;
  account_ref: string | null;
  update_status?: 'updating' | 'success' | 'error';
  update_error?: string;
  uses_rateworks?: boolean;
  has_credential_error?: boolean;
}

export interface MarketCredentialsData {
  status: 'syncing' | 'ready' | 'error';
  fetched_at: string | null;
  error_message: string | null;
  carriers: MarketCredential[];
}

export const apiGetMarketCredentials = (
  quoteId: number,
): Promise<MarketCredentialsData> => {
  return GET(`api/quote-marketplace/quotes/${quoteId}/market-credentials/`);
};

export const apiUpdateBrokerCredential = (
  providerCode: string,
  data: { username: string; password: string },
): Promise<{ status: 'updating'; provider_code: string }> => {
  return POST(
    `api/quote-marketplace/broker/market-credentials/${providerCode}/`,
    data,
  );
};

export type { APIMarketplaceGroupQuote as GroupQuote };

export interface QuoteGroup {
  id: number;
  quotes: APIMarketplaceGroupQuote[];
  created_at: string;
}

export interface QuoteGroupSummaryQuote {
  id: number;
  line_of_business: number;
  line_of_business_display: string;
  effective_date: string;
  has_carrier: boolean;
  carrier_name?: string | null;
  annual_premium?: string | null;
  monthly_premium?: string | null;
}

export interface QuoteGroupSummary {
  id: number;
  quotes: QuoteGroupSummaryQuote[];
  combined_annual_premium: string | null;
  combined_monthly_premium: string | null;
  all_quotes_have_carrier: boolean;
}

export const apiCombineQuotes = (quoteIds: number[]): Promise<QuoteGroup> => {
  return POST('api/quote-marketplace/quotes/combine/', { quote_ids: quoteIds });
};

export const apiUncombineQuote = (
  quoteId: number,
): Promise<{ status: string }> => {
  return POST(`api/quote-marketplace/quotes/${quoteId}/uncombine/`, {});
};

export const apiGetQuoteGroupSummary = (
  groupId: number,
): Promise<QuoteGroupSummary> => {
  return GET(`api/quote-marketplace/groups/${groupId}/summary/`);
};

export interface InvoicingBillingPolicyTerm {
  state: 'standard' | 'non_standard' | 'multi_year' | 'invalid';
  years: number | null;
  months: number | null;
  days: number | null;
  total_days: number | null;
}

export interface InvoicingBilling {
  contact_ref: string;
  rfq_ref: string;
  quote_ref: string;
  product_name: string | null;
  product_target: string | null;
  product_sub_type: string | null;
  quote_status: string | null;
  carrier_name: string | null;
  insurer_account: string | null;
  rating_method: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  policy_term: InvoicingBillingPolicyTerm;
  cover_timezone: string | null;
  province: string | null;
  sales_tax_territory: string | null;
  annual_premium: string | null;
  pro_rata_premium: string | null;
  net_premium: string | null;
  tax_name: string | null;
  tax_rate: string | null;
  tax_applicable: boolean | null;
  tax_amount: string | null;
  total: string | null;
  commission_rate: string | null;
  commission_amount: string | null;
  business_event_type: string | null;
  invoice_account_type: string | null;
  payment_method: string | null;
  post_to: string | null;
  on_account_indicator: string | null;
  insurer_quote_number: string | null;
  policy_number: string | null;
  insurer_fee: string | null;
  commission_fee: string | null;
  admin_fee: string | null;
  agent_fee: string | null;
  insurer_fee_tax_rate: string | null;
  producer: string | null;
  csr: string | null;
}

export const apiGetInvoicingBilling = (
  contactRef: string,
  rfqRef: string,
  quoteRef: string,
): Promise<InvoicingBilling> =>
  GET(
    `api/quote-marketplace/contacts/${contactRef}/rfqs/${rfqRef}/quotes/${quoteRef}/billing/`,
  ).catch((err) => {
    const body = err?.message?.response?.data ?? err;
    throw new Error(
      typeof body?.error === 'string' && body.error !== 'Unhandled error'
        ? body.error
        : '',
    );
  });

export type SubmissionRoute = 'pl_specialty' | 'cl_property';

export interface ProductTargetOption {
  value: string;
  label: string;
}

export const apiGetProductTargets = (
  contactRef: string,
  officeRef: string,
): Promise<{ product_targets: ProductTargetOption[] }> =>
  GET(
    `api/quote-marketplace/product-targets/?contact_ref=${contactRef}&office_ref=${officeRef}`,
  );
